import { DescriptionContentType, DescriptionDialog, isGenerativeDescriptionContentType } from '@tableau/catalog-descriptions';
import React from 'react';
import { call, put } from 'redux-saga/effects';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { assertUnreachable } from '~commonInfra/utils/TypescriptHelpers';
import { CatalogProviders } from '~src/catalog/CatalogProviders';
import { CatalogTelemetryProvider } from '~src/catalog/DataCatalogTelemetry';
import { setDescriptionForItem } from '~src/catalog/restApi/descriptions/setDescriptionForItem';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { DataSources } from '~src/datasource/resources/Datasources';
import { PublishedConnectionTables } from '~src/publishedConnectionTable/resources/PublishedConnectionTables';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { Workbooks } from '~src/workbook/resources/Workbooks';

import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import {
  DescriptionDialogState,
  getDescriptionDialogOptions,
} from '../../../catalog/components/DescriptionDialog';
import { Messages } from '../../../codegen/Localize';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { PublishedConnections } from '../../../publishedConnection/resources/PublishedConnections';
import { requestSpaceContents } from '../../actions/RequestSpaceContentsActions';
import { SetDescriptionAction } from '../../actions/SetDescriptionAction';
import { IContentInfo } from '../../models/IContentInfo';
import { reloadSpaceIfUpdatingCurrentSpace } from './RefreshContentSagas';
import { isEadDescribableContentType, sendDescriptionUpdatedTelemetry } from './SetDescriptionSagaTelemetry';

export function* handleSetDescription(action: SetDescriptionAction) {
  try {
    yield call(openDescriptionDialog, action.item);

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

type DescriptionItem = IContentInfo<'virtualConnection'> | IContentInfo<'virtualConnectionTable'> | IContentInfo<'database'> | IContentInfo<'workbook'>| IContentInfo<'table'>  | IContentInfo<'datasource'>

function setDescription(
  item: DescriptionItem,
  state: DescriptionDialogState
): Promise<IResult> {
  // Pre-existing guard had `Promise.reject();` with no return -- the rejection was created
  // and dropped, execution fell through, and the switch below would call
  // setDescriptionForItem with `luid: undefined`, producing an HTTP 404/400 on a URL like
  // `tables/undefined`. Fix: return the rejection so the caller sees the precondition failure.
  if ((item.contentType === 'database' || item.contentType === 'table') && !item.luid) {
    return Promise.reject(new Error(
      `setDescription: ${item.contentType} requires a luid; got undefined`,
    ));
  }

  const updatePromise: Promise<IResult> = (() => {
    switch (item.contentType) {
      case 'database': return setDescriptionForItem({
        contentType: 'databases',
        luid: item.luid,
        body: {
          database: {
            description: state.description,
          }
        }
      });
      case 'table': return setDescriptionForItem({
        contentType: 'tables',
        luid: item.luid,
        body: {
          table: {
            description: state.description,
          }
        }
      });
      case 'virtualConnection': return PublishedConnections.setDescription({
        description: state.description,
        id: item.id,
      });
      case 'virtualConnectionTable': return PublishedConnectionTables.setDescription({
        description: state.description,
        id: item.id,
      });
      case 'datasource':
        return DataSources.setDescription(item.id, state.description);
      case 'workbook':
        return Workbooks.setDescription(item.id, state.description);
      default: assertUnreachable(item);
    }
  })();

  return updatePromise.then(result => {
    // Emit the PFT event only on a successful save so the catalog-ui and vpc halves of the
    // shared description_updated.completed Juno schema agree that `.completed` == success.
    // EAD content-type filter is applied here so workbook/datasource updates (out of scope
    // for the EAD PFT feature) don't show up in the funnel.
    if (isEadDescribableContentType(item.contentType) && (!result?.errors || result.errors.length === 0)) {
      sendDescriptionUpdatedTelemetry({
        contentType: item.contentType,
        item,
        previousDescription: item.description ?? '',
        newDescription: state.description,
        source: 'manual',
      });
    }
    return result;
  });
}

export function openDescriptionDialog(item: DescriptionItem): PromiseLike<unknown> {
  let maxCharacterLimit = getSessionManagementServiceInstance().maxDescriptionLength();
  let useMarkup = true;

  switch(item.contentType) {
    case 'datasource':
    case 'database':
    case 'workbook':
    case 'table':
      break;
    case 'virtualConnection':
    case 'virtualConnectionTable':
      useMarkup = false;
      break;
    default: assertUnreachable(item);
  }

  if (FeatureFlags.suggestDescriptionsEnabled && item.luid && (isGenerativeDescriptionContentType(item.contentType) || item.contentType === 'database')) {
    return openGenerativeDescriptionDialog(item.contentType, item.luid, item.description || '');
  }

  const modal = openReactActionDialog(getDescriptionDialogOptions({
    initialBodyProps: { useMarkup, maxCharacterLimit },
    initialFormData: { description: item.description || '' },
    doAction: (formData) => setDescription(item, formData)
  }));

  return modal.result;
}

function openGenerativeDescriptionDialog(contentType: DescriptionContentType, luid: string, description?: string) {
  return ReactModalService.openCustom(({ close }) => {
    return (
      <CatalogProviders>
        <CatalogTelemetryProvider>
          <DescriptionDialog contentId={luid} contentType={contentType} hideDialog={close} description={description} testId='generative-description' />
        </CatalogTelemetryProvider>
      </CatalogProviders>
    );
  }).result;
}
